import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import TrustBadge from '../components/TrustBadge';
import { FiCheckCircle, FiShield, FiTruck, FiAward } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchFeaturedReviews();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedReviews = async () => {
    try {
      // Fetch reviews for a few products
      const reviewsPromises = ['reta-10', 'ghk-cu-50', 'slk-10'].map(productId =>
        axios.get(`${API}/products/${productId}/reviews`)
      );
      const reviewsResponses = await Promise.all(reviewsPromises);
      const allReviews = reviewsResponses.flatMap(res => res.data);
      // Get 3 most recent reviews
      setFeaturedReviews(allReviews.slice(0, 3));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const coaImages = [
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/og5554iw_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/70puutxy_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/2ybl75h8_image.png',
    'https://customer-assets.emergentagent.com/job_aps-research/artifacts/pwy10jof_image.png',
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tighter text-primary-main mb-6" data-testid="hero-headline">
                High-Purity Research Peptides — Australian Supply
              </h1>
              <p className="text-lg text-text-secondary mb-8 leading-relaxed">
                Precision-tested compounds with verified Certificates of Analysis and fast domestic shipping.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/shop')}
                  className="btn-primary"
                  data-testid="hero-shop-button"
                >
                  Shop Peptides
                </button>
                <button
                  onClick={() => navigate('/quality')}
                  className="btn-secondary"
                  data-testid="hero-coa-button"
                >
                  View COAs
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_aps-research/artifacts/i5ovfcxf_Screenshot%202026-03-21%20114139.png"
                alt="Precision Peptides AU Logo"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-background-subtle" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <TrustBadge
              icon={<FiCheckCircle />}
              title="Third-Party Tested"
              description="Independent lab verification"
            />
            <TrustBadge
              icon={<FiAward />}
              title="≥98% Purity"
              description="Pharmaceutical-grade standards"
            />
            <TrustBadge
              icon={<FiShield />}
              title="Transparent COA"
              description="Full batch documentation"
            />
            <TrustBadge
              icon={<FiTruck />}
              title="Fast AU Shipping"
              description="Domestic express delivery"
            />
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 lg:py-32" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tighter text-primary-main mb-4">
              Featured Products
            </h2>
            <p className="text-lg text-text-secondary">
              Research-grade peptides with verified purity and quality
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12" data-testid="loading-indicator">
              <p className="text-text-muted">Loading products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/shop')}
              className="btn-secondary"
              data-testid="view-all-products-button"
            >
              View All Products
            </button>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      {featuredReviews.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tighter text-primary-main mb-4">
                Customer Reviews
              </h2>
              <p className="text-lg text-text-secondary">
                What our customers say about our research-grade peptides
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredReviews.map((review) => (
                <div key={review.id} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-shadow" data-testid={`featured-review-${review.id}`}>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'text-warning fill-current' : 'text-slate-300'}`}
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-text-secondary leading-relaxed mb-4 line-clamp-4">"{review.comment}"</p>
                  <div className="border-t border-slate-100 pt-4">
                    <p className="font-heading font-semibold text-primary-main text-sm">{review.customer_name}</p>
                    {review.verified_purchase && (
                      <p className="text-xs text-success mt-1">Verified Purchase</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quality Section */}
      <section className="py-24 lg:py-32 bg-background-subtle" data-testid="quality-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tighter text-primary-main mb-6">
                Verified Testing & Transparency
              </h2>
              <p className="text-lg text-text-secondary mb-6 leading-relaxed">
                Every batch is independently tested by reputable third-party laboratories. 
                Our Certificates of Analysis are available to all customers, ensuring complete transparency.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-success w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-heading font-semibold text-text-primary mb-1">Independent Lab Testing</h4>
                    <p className="text-sm text-text-secondary">
                      Verified by Janoshik Analytical and other accredited facilities
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-success w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-heading font-semibold text-text-primary mb-1">Batch Verification</h4>
                    <p className="text-sm text-text-secondary">
                      Each product linked to specific batch numbers and test results
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="text-success w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-heading font-semibold text-text-primary mb-1">QR Verification</h4>
                    <p className="text-sm text-text-secondary">
                      Scan COA QR codes to verify authenticity directly with testing labs
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/quality')}
                className="btn-primary mt-8"
                data-testid="learn-more-quality-button"
              >
                Learn More About Our Quality Standards
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {coaImages.map((img, idx) => (
                <div key={idx} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <img src={img} alt={`COA ${idx + 1}`} className="w-full h-auto rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
